import {
  Applications,
  CHECKD_PRIMARY_THEME,
  DASHD_PRIMARY_THEME,
  DEFAULT_PRIMARY_THEME,
  zApplications,
} from '@idu-nightlife/ui-kit';
import { isLoopbackHostname, type SiblingAppId } from '@idu-nightlife/nightlife-utils';
import type { FunctionComponent, SVGProps } from 'react';
import { CONFIG } from '../../../config';

import Nightlife from '../../assets/idu-inline.svg?react';
import Checkd from '../../assets/checkd-inline.svg?react';
import Dashd from '../../assets/dashd-inline.svg?react';

export type NightlifeAppId = `nightlife-${string}`;

export const NIGHTLIFE_DASHBOARD_APP_ID = 'nightlife_dashboard';

export interface AppConfig {
  id: Applications | NightlifeAppId | typeof NIGHTLIFE_DASHBOARD_APP_ID;
  name: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>> | undefined;
  url: string | undefined;
  color: string;
}

const resolveLocalAppUrl = (url: string | undefined) => {
  if (!url || typeof window === 'undefined' || !isLoopbackHostname(window.location.hostname)) {
    return url;
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(url);
  } catch {
    return url;
  }

  if (parsedUrl.protocol === 'http:' && parsedUrl.hostname.endsWith('.local')) {
    parsedUrl.hostname = window.location.hostname;
  }

  return parsedUrl.toString();
};

/**
 * A preview deploy writes its sibling channel URLs into `config.js` once every app is up
 * (spec 029 D9) — those win. Everywhere else the per-environment build values apply.
 */
const resolveSiblingAppUrl = (app: SiblingAppId, environmentUrl: string | undefined) =>
  resolveLocalAppUrl(CONFIG.SIBLING_APP_ORIGINS?.[app] ?? environmentUrl);

/**
 * The trusted origin for a configured app — used to address and validate
 * cross-window messages (never '*'). Derived from the per-environment URL,
 * including the local loopback rewrite.
 */
export function getAppOrigin(config: AppConfig): string | undefined {
  if (!config.url) {
    return undefined;
  }

  try {
    return new URL(config.url).origin;
  } catch {
    return undefined;
  }
}

/**
 * Whether this deployment can address the app at all. A preview that excludes an app (Trackd
 * is parked out of previews) leaves its URL unset — such an app must never be selectable.
 */
export function hasResolvableUrl(config: AppConfig): config is AppConfig & { url: string } {
  return getAppOrigin(config) !== undefined;
}

export type NightlifeAppConfig = AppConfig & {
  id: NightlifeAppId;
  key: string;
  url: string;
  apiUrl: string;
};

/** One embedded manager portal per nightlife project, each listing its venues through its own Express API. */
export const nightlifeConfigs: NightlifeAppConfig[] = CONFIG.NIGHTLIFE_INSTANCES.map(
  ({ key, portalUrl, apiUrl }) => ({
    id: `nightlife-${key}`,
    key,
    name: 'Nightlife',
    icon: Nightlife,
    url: resolveLocalAppUrl(portalUrl) ?? portalUrl,
    apiUrl: resolveLocalAppUrl(apiUrl) ?? apiUrl,
    color: DEFAULT_PRIMARY_THEME.darkest ?? 'black',
  }),
);

/** The nightlife staff dashboard, one deploy for every project; iDU Dashboard admins open it per project. */
export const nightlifeDashboardConfig: AppConfig = {
  id: NIGHTLIFE_DASHBOARD_APP_ID,
  name: 'Nightlife Dashboard',
  icon: Nightlife,
  url: resolveLocalAppUrl(CONFIG.NIGHTLIFE_DASHBOARD_URL),
  color: DASHD_PRIMARY_THEME.main,
};

export const configs: AppConfig[] = [
  ...nightlifeConfigs,
  nightlifeDashboardConfig,
  {
    id: zApplications.enum.checkd,
    name: 'Checkd',
    icon: Checkd,
    url: resolveSiblingAppUrl('checkd', CONFIG.CHECKD_URL),
    color: CHECKD_PRIMARY_THEME.main,
  },
  {
    id: zApplications.enum.idu_dashboard,
    name: 'iDU Dashboard',
    icon: Dashd,
    url: resolveSiblingAppUrl('idu-dashboard', CONFIG.IDU_DASHBOARD_URL),
    color: DASHD_PRIMARY_THEME.main,
  },
];
